// components/AddExpense.jsx
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Loader from './Loader';
import Sidebar from './Sidebar';
import { toast } from 'react-toastify';
import { Trash2, Edit } from 'lucide-react';
import { getAllExpenseItems, saveExpenseItem, deleteExpenseItem, updateExpenseItem } from '../utils/firebaseFunctions'; // Adjust path
import { FaCarrot, FaRupeeSign } from 'react-icons/fa'; // For rupee icon
import { IoCashOutline } from 'react-icons/io5';
import { GiForkKnifeSpoon } from 'react-icons/gi';
const AddExpense = () => {
    const [expenseType, setExpenseType] = useState('food'); // 'food' or 'monthly'
    const [nameOrCategory, setNameOrCategory] = useState('');
    const [amount, setAmount] = useState(''); // e.g., "5kg per day" for food, or actual amount for monthly
    const [monthlyAmount, setMonthlyAmount] = useState(''); // Only for monthly expenses
    const [pricePerUnit, setPricePerUnit] = useState(''); // Only for food items
    const [descriptionOrDetails, setDescriptionOrDetails] = useState(''); // For both types
    const [isLoading, setIsLoading] = useState(false);
    const [field, setField] = useState(false);
    const [alertStatus, setAlertStatus] = useState('danger');
    const [msg, setMsg] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    // Lists for displaying current items
    const [foodItemsList, setFoodItemsList] = useState([]);
    const [monthlyExpensesList, setMonthlyExpensesList] = useState([]);

    // State for edit modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentEditItem, setCurrentEditItem] = useState(null);
    const [editExpenseType, setEditExpenseType] = useState('');
    const [editNameOrCategory, setEditNameOrCategory] = useState('');
    const [editAmount, setEditAmount] = useState(''); // Used for quantity (food) or monthly amount (monthly)
    const [editMonthlyAmount, setEditMonthlyAmount] = useState(''); // New state for editing monthly amount specifically
    const [editPricePerUnit, setEditPricePerUnit] = useState('');
    const [editDescriptionOrDetails, setEditDescriptionOrDetails] = useState('');

    const toggleSidebar = (state) => {
        setIsSidebarOpen(state);
    };

    const fetchAllExpenses = async () => {
        try {
            const food = await getAllExpenseItems('foodExpenses');
            const monthly = await getAllExpenseItems('monthlyExpenses');
            setFoodItemsList(food);
            setMonthlyExpensesList(monthly);
        } catch (error) {
            console.error("Error fetching expenses:", error);
            toast.error("Failed to fetch expenses.");
        }
    };

    const clearForm = () => {
        setNameOrCategory('');
        setAmount('');
        setMonthlyAmount('');
        setPricePerUnit('');
        setDescriptionOrDetails('');
        setField(false);
    };

const saveDetails = async () => {
    setIsLoading(true);
    setField(false); // Reset field status

    const collectionName = expenseType === 'food' ? 'foodExpenses' : 'monthlyExpenses';

    // --- Validation based on type ---
    if (!nameOrCategory || !descriptionOrDetails) {
        setField(true);
        setMsg("Required fields can't be empty");
        setAlertStatus("danger");
        toast.error("Required fields can't be empty");
        setTimeout(() => {
            setField(false);
            setIsLoading(false);
        }, 4000);
        return;
    }

    if (expenseType === 'food' && (!amount || !pricePerUnit)) {
        setField(true);
        setMsg("Required fields for food items (Quantity, Price Per Unit) can't be empty");
        setAlertStatus("danger");
        toast.error("Required fields for food items (Quantity, Price Per Unit) can't be empty");
        setTimeout(() => {
            setField(false);
            setIsLoading(false);
        }, 4000);
        return;
    }

    // Now, monthlyAmount is an input for both, but only strictly required for 'monthly'
    // if you want it to be optional for 'food'.
    if (expenseType === 'monthly' && !monthlyAmount) {
        setField(true);
        setMsg("Monthly amount is required for monthly expenses.");
        setAlertStatus("danger");
        toast.error("Monthly amount is required for monthly expenses.");
        setTimeout(() => {
            setField(false);
            setIsLoading(false);
        }, 4000);
        return;
    }
    // --- End Validation ---

    try {
        const id = `${Date.now()}`;
        // Start with common fields and ensure monthlyAmount is null if empty, not undefined
        let dataToSave = {
            id: id,
            timestamp: Date.now(),
            monthlyAmount: monthlyAmount || null // Use null instead of undefined for empty input
        };

        if (expenseType === 'food') {
            // Add specific fields for food items
            dataToSave = {
                ...dataToSave, // Keep common fields
                name: nameOrCategory,
                amount: amount, // Quantity for food
                description: descriptionOrDetails,
                pricePerUnit: pricePerUnit,
                // Calculate monthlyExpense for food items
                monthlyExpense: `₹${(parseInt(amount.replace(/[^\d]/g, '')) || 0) * (parseInt(pricePerUnit.replace(/[^\d]/g, '')) || 0) * 30}`
            };
        } else { // expenseType === 'monthly'
            // Add specific fields for monthly expenses
            dataToSave = {
                ...dataToSave, // Keep common fields
                category: nameOrCategory,
                details: descriptionOrDetails,
                // monthlyAmount is already included from dataToSave base
            };
        }

        await saveExpenseItem(dataToSave, collectionName); // Use the correctly built object
        setIsLoading(false);
        setField(true);
        setMsg("Details saved successfully");
        setAlertStatus("success");
        toast.success("Details saved successfully");
        clearForm();
        setTimeout(() => {
            setField(false);
            fetchAllExpenses(); // Refresh the lists
        }, 1000);
    } catch (error) {
        console.error("Error saving details:", error);
        toast.error("Failed to save details");
        setIsLoading(false);
        setField(true);
        setMsg("Error saving details");
        setAlertStatus("danger");
        setTimeout(() => {
            setField(false);
        }, 4000);
    }
};
    const handleDelete = async (docId, type) => {
         const confirmDelete = window.confirm("Are you sure you want to delete this item? This action cannot be undone.");
        if (!confirmDelete) {
            return; // If the user cancels, stop the function
        }

        setIsLoading(true);
        const collectionName = type === 'food' ? 'foodExpenses' : 'monthlyExpenses';
        try {
            await deleteExpenseItem(docId, collectionName);
            toast.success("Deleted successfully");
            fetchAllExpenses(); // Refresh list
        } catch (error) {
            console.error("Error deleting item:", error);
            toast.error("Failed to delete item");
        } finally {
            setIsLoading(false);
        }
    };

    // 🆕 Open Edit Modal
    const handleEditClick = (item, type) => {
        setCurrentEditItem(item);
        setEditExpenseType(type);
        setEditNameOrCategory(item.name || item.category);
        // Set editAmount for food items (quantity) and editMonthlyAmount for monthly expenses
        setEditAmount(type === 'food' ? item.amount || '' : '');
       setEditMonthlyAmount(item.monthlyAmount || ''); 
        setEditPricePerUnit(item.pricePerUnit || '');
        setEditDescriptionOrDetails(item.description || item.details);
        setIsEditModalOpen(true);
    };

    // 🆕 Close Edit Modal
    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setCurrentEditItem(null);
        setEditNameOrCategory('');
        setEditAmount('');
        setEditMonthlyAmount(''); // Clear monthly amount state
        setEditPricePerUnit('');
        setEditDescriptionOrDetails('');
        setEditExpenseType('');
    };

const handleUpdateDetails = async () => {
    if (!currentEditItem) return;

    setIsLoading(true);
    const collectionName = editExpenseType === 'food' ? 'foodExpenses' : 'monthlyExpenses';

    // Validation for edit (Keep your existing validation logic)
    if (!editNameOrCategory || !editDescriptionOrDetails) {
        toast.error("Required fields can't be empty");
        setIsLoading(false);
        return;
    }

    if (editExpenseType === 'food' && (!editAmount || !editPricePerUnit)) {
        toast.error("Required fields for food items (Quantity, Price Per Unit) can't be empty");
        setIsLoading(false);
        return;
    }

    // Since monthlyAmount is now available for both, adjust validation.
    // If it's mandatory for monthly, keep this check.
    // If it's optional for food, don't include it in food validation.
    if (editExpenseType === 'monthly' && !editMonthlyAmount) {
        toast.error("Required fields for monthly expenses (Monthly Amount) can't be empty");
        setIsLoading(false);
        return;
    }

    try {
        // Start with common fields and ensure monthlyAmount is null if empty
        let updatedData = {
            monthlyAmount: editMonthlyAmount || null // Use null for empty string/falsy values
        };

        if (editExpenseType === 'food') {
            // Add specific fields for food items
            updatedData = {
                ...updatedData, // Include common fields like monthlyAmount
                name: editNameOrCategory,
                amount: editAmount, // Assuming 'amount' is still the state name for quantity
                description: editDescriptionOrDetails,
                pricePerUnit: editPricePerUnit,
                monthlyExpense: `₹${(parseInt(editAmount.replace(/[^\d]/g, '')) || 0) * (parseInt(editPricePerUnit.replace(/[^\d]/g, '')) || 0) * 30}`
            };
        } else { // editExpenseType === 'monthly'
            // Add specific fields for monthly expenses
            updatedData = {
                ...updatedData, // Include common fields like monthlyAmount
                category: editNameOrCategory,
                details: editDescriptionOrDetails,
                // monthlyAmount is already in the base updatedData object
            };
        }

        await updateExpenseItem(currentEditItem.docId, updatedData, collectionName);
        toast.success("Details updated successfully");
        setIsLoading(false);
        handleCloseEditModal();
        fetchAllExpenses(); // Refresh the lists after updating
    } catch (error) {
        console.error("Error updating details:", error);
        toast.error("Failed to update details");
        setIsLoading(false);
    }
};
    useEffect(() => {
        fetchAllExpenses();
    }, []);
    return (
        <div className='lg:flex h-screen bg-white'>
            <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={`flex-1 p-1 lg:p-12 overflow-y-auto transition-all duration-300 lg:mt-2 mt-16 ${isSidebarOpen ? 'ml-56 p-0' : 'lg:ml-20 sm:ml-2 '}`}>
                <div className={`w-full max-w-4xl mx-auto bg-white shadow-xl rounded-2xl lg:p-8 ${isSidebarOpen ? ' p-1' : 'lg:p-8 p-8'}`}>
                    <h1 className={`lg:text-2xl font-bold text-[#fcaf17] mb-6 ${isSidebarOpen ? 'text-[13px]' : ''}`}>Add New Expense Item</h1>

                    {field && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className={`p-3 rounded-lg text-center text-lg font-medium ${alertStatus === "danger" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"}`}
                        >
                            {msg}
                        </motion.p>
                    )}

                    <div className='lg:space-y-3 space-y-1'>
                        {/* Expense Type Selector */}
                        <div className="mb-4">
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`}>
                                Select Expense Type:
                            </label>
                            <div className="flex items-center gap-4">
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="form-radio text-blue-600"
                                        name="expenseType"
                                        value="food"
                                        checked={expenseType === 'food'}
                                        onChange={() => setExpenseType('food')}
                                    />
                                    <span className={`ml-2 text-gray-700 ${isSidebarOpen ? 'text-xs' : 'text-sm'}`}>Food Item</span>
                                </label>
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="form-radio text-blue-600"
                                        name="expenseType"
                                        value="monthly"
                                        checked={expenseType === 'monthly'}
                                        onChange={() => setExpenseType('monthly')}
                                    />
                                    <span className={`ml-2 text-gray-700 ${isSidebarOpen ? 'text-xs' : 'text-sm'}`}>Monthly Expense</span>
                                </label>
                            </div>
                        </div>

                        {/* Name/Category Input */}
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='nameOrCategory'>
                                {expenseType === 'food' ? 'Food Item Name' : 'Expense Category'}
                            </label>
                            <input
                                id='nameOrCategory'
                                type='text'
                                value={nameOrCategory}
                                autoComplete='off'
                                placeholder={expenseType === 'food' ? 'e.g., Green Fodder' : 'e.g., Medical Care'}
                                onChange={(e) => setNameOrCategory(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>

                          {expenseType === 'food' && (
                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='amount'>
                                 Quantity
                            </label>
                            <input
                                id='amount'
                                type='text'
                                value={amount}
                                autoComplete='off'
                                placeholder={expenseType === 'food' ? 'e.g., 5kg per day' : 'e.g., ₹45,000'}
                                onChange={(e) => setAmount(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>
                          )}
                        {/* Price Per Unit (only for Food Items) */}
                        {expenseType === 'food' && (
                            <div>
                                <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='pricePerUnit'>
                                    Price Per Unit
                                </label>
                                <input
                                    id='pricePerUnit'
                                    type='text'
                                    value={pricePerUnit}
                                    autoComplete='off'
                                    placeholder='e.g., ₹12 per kg'
                                    onChange={(e) => setPricePerUnit(e.target.value)}
                                    className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                                />
                            </div>
                        )}

                        <div>
                            <label className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`} htmlFor='monthlyAmount'>
                                 Monthly Amount
                            </label>
                            <input
                                id='monthlyAmount'
                                type='text'
                                value={monthlyAmount}
                                autoComplete='off'
                                placeholder={expenseType === 'food' ? 'e.g., ₹45,000' : 'e.g., ₹45,000'}
                                onChange={(e) => setMonthlyAmount(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none ${isSidebarOpen ? 'text-xs' : ''}`}
                            />
                        </div>
                          
                        {/* Description/Details Input */}
                        <div>
                            <label
                                className={`block text-gray-700 text-sm font-medium mb-2 ${isSidebarOpen ? 'text-xs' : ''}`}
                                htmlFor="descriptionOrDetails"
                            >
                                {expenseType === 'food' ? 'Description' : 'Details'}
                            </label>
                            <textarea
                                id="descriptionOrDetails"
                                value={descriptionOrDetails}
                                autoComplete="off"
                                placeholder={expenseType === 'food' ? 'Brief description of the food item' : 'Detailed breakdown of the expense category'}
                                onChange={(e) => setDescriptionOrDetails(e.target.value)}
                                className={`w-full px-4 py-2 border border-gray-300 lg:text-md text-sm rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none ${isSidebarOpen ? 'text-xs' : ''}`}
                                rows="3"
                            />
                        </div>

                        {/* Save Button */}
                        <div className="flex justify-start">
                            <button
                                className="bg-blue-600 text-white lg:text-sm text-xs py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-300"
                                onClick={saveDetails}
                            >
                                {isLoading ? <Loader /> : 'Save Expense'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- Food Items Table --- */}
                {foodItemsList.length > 0 && (
                    <div className="mt-12 p-8 transform transition-all duration-300 hover:shadow-yellow-300/40">
                        <h2 className="lg:text-2xl text-xl font-extrabold mb-8 text-[#fcaf17] tracking-wide flex items-center gap-3 animate-fade-in-down">
                            <span><FaCarrot /></span> Food Items
                        </h2>
                        <div className="overflow-x-auto border border-yellow-200">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-yellow-100 to-yellow-200/70 backdrop-blur-sm border-b border-yellow-300 text-[14px] uppercase font-bold tracking-wider text-orange-800">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Name</th>
                                        <th className="px-6 py-4">Quantity</th>
                                        <th className="px-6 py-4">Price/Unit</th>
                                        <th className="px-6 py-4">Monthly Expense</th>
                                        <th className="px-6 py-4">Description</th>
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {foodItemsList.map((item, index) => (
                                        <tr key={item.docId} className="group bg-white/70 transition-all duration-300 hover:bg-orange-50/70 border-b border-orange-100 last:border-b-0">
                                            <td className="px-6 py-4 font-semibold text-gray-800 text-base">{index + 1}</td>
                                            <td className="px-6 py-4 text-gray-900 font-medium">{item.name}</td>
                                            <td className="px-6 py-4">{item.amount}</td>
                                            <td className="px-6 py-4">{item.pricePerUnit}</td>
                                            <td className="px-6 py-4 font-bold text-orange-600">{item.monthlyAmount}</td>
                                            <td className="px-6 py-4 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">{item.description}</td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEditClick(item, 'food')}
                                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-3 py-1.5 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                                        title="Edit Food Item"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.docId, 'food')}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-3 py-1.5 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300"
                                                        title="Remove Food Item"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* --- Monthly Expenses Table --- */}
                {monthlyExpensesList.length > 0 && (
                    <div className="mt-12 p-8 transform transition-all duration-300 hover:shadow-green-300/40">
                        <h2 className="lg:text-2xl text-xl font-extrabold mb-8 text-[#17a34a] tracking-wide flex items-center gap-3 animate-fade-in-down">
                            <span> <IoCashOutline/> </span> Monthly Expenses
                        </h2>
                        <div className="overflow-x-auto border border-green-200 ">
                            <table className="min-w-full text-sm text-left text-gray-700">
                                <thead className="bg-gradient-to-r from-green-100 to-green-200/70 backdrop-blur-sm border-b border-green-300 text-[14px] uppercase font-bold tracking-wider text-green-800">
                                    <tr>
                                        <th className="px-6 py-4">Sl. No.</th>
                                        <th className="px-6 py-4">Category</th>
                                        <th className="px-6 py-4">Monthly Amount</th>
                                        <th className="px-6 py-4">Details</th>
                                        <th className="px-6 py-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthlyExpensesList.map((item, index) => (
                                        <tr key={item.docId} className="group bg-white/70 transition-all duration-300 hover:bg-green-50/70 border-b border-green-100 last:border-b-0">
                                            <td className="px-6 py-4 font-semibold text-gray-800 text-base">{index + 1}</td>
                                            <td className="px-6 py-4 text-gray-900 font-medium">{item.category}</td>
                                            <td className="px-6 py-4 font-bold text-green-600">{item.monthlyAmount}</td>
                                            <td className="px-6 py-4 max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">{item.details}</td>
                                            <td className="px-6 py-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEditClick(item, 'monthly')}
                                                        className="inline-flex items-center gap-2 text-blue-600 hover:text-white bg-blue-100 hover:bg-blue-500 px-3 py-1.5 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                                        title="Edit Monthly Expense"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(item.docId, 'monthly')}
                                                        className="inline-flex items-center gap-2 text-red-600 hover:text-white bg-red-100 hover:bg-red-500 px-3 py-1.5 rounded-full text-sm font-semibold shadow-md transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300"
                                                        title="Remove Monthly Expense"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {foodItemsList.length === 0 && monthlyExpensesList.length === 0 && (
                    <div className="mt-12 p-8 text-center text-gray-500 text-lg italic bg-white border border-orange-100 rounded-xl shadow-sm">
                        No expenses to display yet. Let's add some!
                    </div>
                )}


                {/* --- Edit Modal --- */}
                {isEditModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="bg-white p-8 rounded-lg shadow-xl w-full max-w-md mx-4"
                        >
                            <h2 className="text-2xl font-bold text-[#fcaf17] mb-6">
                                Edit {editExpenseType === 'food' ? 'Food Item' : 'Monthly Expense'}
                            </h2>
                            <div className="space-y-4">
                                {/* Name/Category Input */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editNameOrCategory'>
                                        {editExpenseType === 'food' ? 'Food Item Name' : 'Expense Category'}
                                    </label>
                                    <input
                                        id='editNameOrCategory'
                                        type='text'
                                        value={editNameOrCategory}
                                        autoComplete='off'
                                        onChange={(e) => setEditNameOrCategory(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>

                                {/* Amount Input */}
                                {editExpenseType === 'food' && (
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editAmount'>
                                     Quantity
                                    </label>
                                    <input
                                        id='editAmount'
                                        type='text'
                                        value={editAmount}
                                        autoComplete='off'
                                        onChange={(e) => setEditAmount(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                )}
                                {/* Price Per Unit (only for Food Items) */}
                                {editExpenseType === 'food' && (
                                    <div>
                                        <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editPricePerUnit'>
                                            Price Per Unit
                                        </label>
                                        <input
                                            id='editPricePerUnit'
                                            type='text'
                                            value={editPricePerUnit}
                                            autoComplete='off'
                                            onChange={(e) => setEditPricePerUnit(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editMonthlyAmount'>
                                     Monthly Amount
                                    </label>
                                    <input
                                        id='editMonthlyAmount'
                                        type='text'
                                        value={editMonthlyAmount}
                                        autoComplete='off'
                                        onChange={(e) => setEditMonthlyAmount(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                {/* Description/Details Input */}
                                <div>
                                    <label className="block text-gray-700 text-sm font-medium mb-2" htmlFor='editDescriptionOrDetails'>
                                        {editExpenseType === 'food' ? 'Description' : 'Details'}
                                    </label>
                                    <textarea
                                        id='editDescriptionOrDetails'
                                        value={editDescriptionOrDetails}
                                        autoComplete='off'
                                        onChange={(e) => setEditDescriptionOrDetails(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-800 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                                        rows="4"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    onClick={handleCloseEditModal}
                                    className="bg-gray-300 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateDetails}
                                    className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-300"
                                >
                                    {isLoading ? <Loader /> : 'Update'}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
                {/* You might need to define these keyframe animations in your global CSS or a dedicated CSS file if you haven't already */}
                <style jsx>{`
                    @keyframes fade-in-down {
                        from {
                            opacity: 0;
                            transform: translateY(-20px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes slide-in-top {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    @keyframes fade-in {
                        from {
                            opacity: 0;
                        }
                        to {
                            opacity: 1;
                        }
                    }

                    .animate-fade-in-down {
                        animation: fade-in-down 0.8s ease-out forwards;
                    }

                    .animate-slide-in-top {
                        animation: slide-in-top 0.6s ease-out forwards;
                    }

                    .animate-fade-in {
                        animation: fade-in 0.5s ease-out forwards;
                    }
                `}</style>
            </div>
        </div>
    );
};

export default AddExpense;